import { useCallback, useEffect, useRef, useState } from 'react';
import { toUserMessage } from '@platform/api-client';
import { APP_HEADER } from '@/shared/components/layout/appHeader.constant.ts';
import { EditorContent, useEditor, useEditorState, type JSONContent } from '@tiptap/react';
import { createBoardExtensions } from './editorExtensions.ts';
import { EditorToolbar } from './EditorToolbar.tsx';
import { useBoardImageUpload } from '../hooks/useBoardUpload.ts';

interface PostEditorProps {
  /** 초기 본문(수정 시). 생성 시 undefined */
  initialContent?: JSONContent;
  /** 본문 변경 콜백 — 최신 JSON 을 부모로 전달 */
  onChange: (json: JSONContent) => void;
  placeholder?: string;
  /** 업로드 에러 등 알림 (부모가 토스트/Alert 로 표시) */
  onError?: (message: string) => void;
}

/**
 * 게시판 본문 에디터.
 *
 * 핵심 설계:
 *  - 본문 이미지는 base64로 박지 않고, 선택/드롭/붙여넣기 즉시 S3 업로드 →
 *    받은 CDN URL 만 노드에 넣는다(jsonb 비대화 방지).
 *  - onUpdate 에서 editor.getJSON() 을 부모로 올린다(저장은 부모가 담당).
 *  - 비제어(uncontrolled) 방식: content 는 최초 1회만 주입하고 이후 내부 상태로
 *    관리한다. 매 keystroke 마다 부모 → editor 로 되먹이면 커서가 튄다.
 *  - 본문 스타일은 뷰어와 같은 `.board-content`(index.css)를 써서 작성 모양 = 발행 모양.
 */
export function PostEditor({
  initialContent,
  onChange,
  placeholder,
  onError,
}: PostEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading } = useBoardImageUpload();
  const [fullscreen, setFullscreen] = useState(false);

  const editor = useEditor({
    extensions: createBoardExtensions({ placeholder, editable: true }),
    content: initialContent ?? '',
    editorProps: {
      attributes: {
        class: 'min-h-[320px] px-4 py-3 focus:outline-none',
      },
      handlePaste: (_view, event) => {
        const file = event.clipboardData?.files?.[0];
        if (file && file.type.startsWith('image/')) {
          void handleImageFile(file);
          return true;
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const file = (event as DragEvent).dataTransfer?.files?.[0];
        if (file && file.type.startsWith('image/')) {
          event.preventDefault();
          void handleImageFile(file);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
  });

  const handleImageFile = useCallback(
    async (file: File) => {
      if (!editor) return;
      try {
        const { media } = await upload({ file });
        if (media?.url) {
          editor.chain().focus().setImage({ src: media.url }).run();
        }
      } catch (err) {
        onError?.(
          toUserMessage(err, '이미지 업로드에 실패했습니다.'),
        );
      }
    },
    [editor, upload, onError],
  );

  const onPickImage = useCallback(() => fileInputRef.current?.click(), []);

  const counts = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            characters: e.storage.characterCount.characters(),
            words: e.storage.characterCount.words(),
          }
        : null,
  });

  // 전체 화면 동안 뒤 페이지가 같이 스크롤되지 않게 막는다.
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleImageFile(file);
      e.target.value = ''; // 같은 파일 재선택 가능하도록 리셋
    },
    [handleImageFile],
  );

  if (!editor) return null;

  return (
    // 툴바 드롭다운이 잘리지 않도록 overflow-hidden 을 두지 않는다(모서리는 툴바·상태줄이 각자 둥글린다).
    // 전체 화면은 사이트 헤더 아래부터 채운다. 맨 위(inset-0)부터 덮으면 고정 헤더(z-50)가
    // 위로 올라와 툴바 윗줄을 가린다(에디터는 MainLayout 의 main, z-10 안에 있다).
    <div
      className={
        fullscreen
          ? `fixed inset-x-0 bottom-0 ${APP_HEADER.stickyTop} z-[100] flex flex-col bg-white dark:bg-dark-base`
          : 'rounded-lg border border-black/15 focus-within:border-neutral-950 dark:border-white/15 dark:focus-within:border-white/40'
      }
    >
      <EditorToolbar
        editor={editor}
        onPickImage={onPickImage}
        imageUploading={isUploading}
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen((v) => !v)}
      />
      <div className={fullscreen ? 'flex-1 overflow-y-auto' : undefined}>
        <EditorContent
          editor={editor}
          className={`board-content ${fullscreen ? 'mx-auto max-w-4xl py-6' : ''}`}
        />
      </div>
      <div
        className={[
          'flex justify-end gap-3 border-t border-black/10 bg-neutral-50 px-3 py-1 text-xs text-neutral-500',
          'dark:border-white/10 dark:bg-dark-surface dark:text-neutral-400',
          fullscreen ? '' : 'rounded-b-lg',
        ].join(' ')}
        aria-live="polite"
      >
        <span>글자 {counts?.characters.toLocaleString() ?? 0}</span>
        <span>단어 {counts?.words.toLocaleString() ?? 0}</span>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFileChange}
      />
    </div>
  );
}
